import { useMemo } from 'react';
import { fetchSubtasks } from '../api/endpoints';
import { toApiError } from '../api/errors';
import { isAbort } from '../api/http';
import type { Id } from '../api/types';
import type { SubtaskIndex, SubtaskIndexEntry } from '../lib/ticket-tree';
import { useResource, type Resource } from './useResource';

/**
 * The children of every parent visible on the current page, fetched per parent.
 *
 * 🔴 WHY A REQUEST PER PARENT AND NOT A DERIVATION FROM THE PAGE. The page is a window over one flat
 * `updated_at DESC` list: a parent can be on page 1 with a child on page 2, and the page alone can
 * never prove it has seen all of a parent's children. Asking `?parentId=` gives a complete answer and
 * a true count.
 *
 * ⭐ The cost is bounded by `parentIdsToFetch`, which asks ONLY for parents this page proves have a
 * child — normally none or one. ⛔ Not one request per row: that is a 50-request client-side N+1,
 * and the API's own list query avoids exactly that shape server-side.
 *
 * ⚠️ `Promise.allSettled`, not `Promise.all`. One parent's children failing must not delete the other
 * parents' children from the screen; the failures are REPORTED instead, and the rows they belong to
 * fall back to the sub-tasks visible on the page with the count marked as a floor.
 */
export interface SubtaskIndexResult {
  index: SubtaskIndex;
  /** Parents whose children could not be loaded. The UI must say so — see BacklogPage. */
  failed: { parentId: Id; reason: string }[];
}

const EMPTY: SubtaskIndexResult = { index: new Map(), failed: [] };

export function useSubtaskIndex(parentIds: readonly Id[]): Resource<SubtaskIndexResult> {
  // A stable primitive key: `deps` is compared by identity, and a fresh array every render would
  // re-fetch forever.
  const key = useMemo(() => [...parentIds].sort().join(','), [parentIds]);

  return useResource<SubtaskIndexResult>(
    async (signal) => {
      const ids = key ? key.split(',') : [];
      if (ids.length === 0) return EMPTY;

      const settled = await Promise.allSettled(ids.map((id) => fetchSubtasks(id, signal)));

      const index = new Map<Id, SubtaskIndexEntry>();
      const failed: SubtaskIndexResult['failed'] = [];

      settled.forEach((outcome, position) => {
        const parentId = ids[position];
        if (parentId === undefined) return;
        if (outcome.status === 'fulfilled') {
          index.set(parentId, { items: outcome.value.items, total: outcome.value.total });
          return;
        }
        // An abort is this component moving on, not a failure to report.
        if (isAbort(outcome.reason)) return;
        const error = toApiError(outcome.reason, '/tickets?parentId');
        failed.push({ parentId, reason: error.reason ?? error.message });
      });

      return { index, failed };
    },
    [key],
    { enabled: parentIds.length > 0 },
  );
}
